import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { Slot } from 'radix-ui'

import { cn } from '@/lib/utils'

// **No font family here.** A button inherits whatever face its surface sets,
// and that is deliberate: the app is serif-led — `body` has been Source Serif
// since the first commit, and the 2026-08-09 Aldus decision ratified that as the
// direction rather than the drift. Nav links, labels, inputs, chips and badges
// are all serif at the same sizes, so a button is the one thing that must not
// opt out.
//
// This base carried `font-sans` for three days (775d346). It was added to fix a
// *landing-page* mismatch — a sans input beside a serif button — but `Button` is
// the app-wide primitive, so it made buttons the only sans element on every
// authenticated screen. Settings was the tell: "Change password" and "Sign out"
// in Source Sans, directly under "Email / Google / Apple" in Source Serif.
//
// Sans is now opt-in per surface, which is where that decision belongs:
// `marketing/*` sets `font-sans` on the two call sites that sit beside sans
// prose, and `admin-shell` sets it once on its container so every admin button
// inherits it. Do not reintroduce a family here to fix one surface.
const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 rounded-md text-sm font-medium whitespace-nowrap transition-all outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive:
          'bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:bg-destructive/60 dark:focus-visible:ring-destructive/40',
        outline:
          'border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost:
          'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        link: 'text-primary underline-offset-4 hover:underline',
        // Shape and states from the base, not one colour. For buttons whose
        // palette is fixed by something outside the theme — a provider's brand
        // guidelines, most of all — where every themed colour would have to be
        // overridden anyway.
        //
        // Overriding is not the same as opting out, and that gap shipped a bug.
        // A variant's `dark:bg-*` and `hover:text-*` sit in different
        // tailwind-merge groups from their bare forms, so a pinned `bg-white`
        // does not displace `dark:bg-input/30` and a pinned `text-neutral-800`
        // does not displace `hover:text-accent-foreground`: both survive, and
        // the prefixed one wins. The Google button rendered its label
        // invisible that way twice — at rest, then on hover. Contributing no
        // colour cannot be out-specified.
        none: '',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        xs: "h-6 gap-1 rounded-md px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: 'h-8 gap-1.5 rounded-md px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-9',
        'icon-xs': "size-6 rounded-md [&_svg:not([class*='size-'])]:size-3",
        'icon-sm': 'size-8',
        'icon-lg': 'size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

function Button({
  className,
  variant = 'default',
  size = 'default',
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot.Root : 'button'

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
